/**
 * UniversalDrilldownModal — opens when any KPI / chart / metric is clicked.
 *
 * Fetches /api/dashboard/drilldown with the provided drill payload, then
 * renders rows in a sortable table. Switches columns based on response type:
 *   - "leads"      → CRM lead columns (nama_account, NPSN, status, assigned, students, value)
 *   - "activities" → Sales activity columns (tanggal, sales, sekolah, kegiatan, via)
 *
 * Drill payload shape: { title, metric, value?, month?, username? }
 *
 * Used by Dashboard.jsx, RoleChartsPanels, AdminMetricsPanel, etc.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Layers, X, Download } from "lucide-react";
import { API } from "../../lib/api";
import { fmtN, fmtDate } from "../../lib/format";

const fmtIDR = (v) =>
  v == null ? "—" :
  v >= 1_000_000_000 ? `Rp ${(v / 1_000_000_000).toFixed(1)} M` :
  v >= 1_000_000     ? `Rp ${(v / 1_000_000).toFixed(1)} Jt` :
  v >= 1_000          ? `Rp ${(v / 1_000).toFixed(0)} rb` :
  `Rp ${fmtN(v)}`;

function exportRowsToCsv(rows, filename) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const STATUS_COLORS = {
  "New Lead": "#94a3b8",
  "Cold Prospect": "#3b82f6",
  "Prospective": "#0ea5e9",
  "Hot Prospect": "#f59e0b",
  "New Account Sign": "#10b981",
  "Existing Account": "#059669",
  "Lost Lead": "#ef4444",
  "Churn Account": "#dc2626",
};

export default function UniversalDrilldownModal({ open, onClose, drill }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [subFilters, setSubFilters] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !drill) return;
    setSubFilters({}); setSearch("");
    // Mode data langsung: menu lain mengirim rows yang sudah dimuat (staticRows)
    if (drill.staticRows) {
      setErr(null);
      setData({ rows: drill.staticRows, type: drill.rowType || "leads", total: drill.staticRows.length });
      setLoading(false);
      return;
    }
    setLoading(true); setErr(null); setData(null);
    const params = { metric: drill.metric, limit: 300 };
    if (drill.value !== undefined && drill.value !== null) params.value = String(drill.value);
    if (drill.month) params.month = drill.month;
    if (drill.username) params.username = drill.username;
    axios.get(`${API}/dashboard/drilldown`, { params })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Gagal memuat data drilldown"))
      .finally(() => setLoading(false));
  }, [open, drill]);

  if (!open) return null;

  const rows = data?.rows || [];
  const type = data?.type || "leads";

  // Sub-category filter fields per tipe data
  const LEAD_FILTER_FIELDS = [
    ["status_lead", "Status"], ["assigned_to", "Assigned"], ["lead_source", "Source"],
    ["course_package", "Package"], ["category", "Category"], ["propinsi", "Propinsi"],
  ];
  const ACT_FILTER_FIELDS = [
    ["nama_sales", "Sales"], ["tim_sales", "Tim"], ["status_sekolah", "Status"],
    ["tujuan_sales", "Tujuan"], ["kegiatan", "Kegiatan"], ["dilakukan_via", "Via"],
  ];
  const fields = type === "leads" ? LEAD_FILTER_FIELDS : ACT_FILTER_FIELDS;
  const optionsFor = (k) => Array.from(new Set(
    rows.map((r) => String(r[k] ?? "")).filter((v) => v && v !== "undefined" && v !== "None")
  )).sort();
  const filteredRows = rows.filter((r) => {
    for (const [k] of fields) {
      if (subFilters[k] && String(r[k] ?? "") !== subFilters[k]) return false;
    }
    if (search) {
      const hay = Object.values(r).map((v) => String(v ?? "")).join(" ").toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  // Summary stats — only for leads
  let summary = null;
  if (type === "leads" && filteredRows.length > 0) {
    const studentsSum = filteredRows.reduce((a, r) => a + (r.jumlah_student || 0), 0);
    const valueSum = filteredRows.reduce((a, r) => a + (r.estimated_value || 0), 0);
    summary = { count: filteredRows.length, students: studentsSum, value: valueSum };
  } else if (type === "activities") {
    summary = { count: filteredRows.length, total: data?.total };
  }

  const onDownloadCsv = () => {
    const fname = `drilldown_${drill?.metric || "data"}_${new Date().toISOString().slice(0,10)}.csv`;
    exportRowsToCsv(filteredRows, fname);
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="universal-drilldown-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(8,15,40,.68)",
                zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center",
                padding: 20, backdropFilter: "blur(6px)" }}>
      <div className="card card-3d" style={{ width: "min(1180px, 100%)", maxHeight: "92vh",
              background: "var(--surface)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 10,
              borderBottom: "1px solid var(--border-2)", padding: "14px 18px" }}>
          <Layers size={18} color="var(--accent)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: 16, color: "var(--navy)" }} data-testid="drill-title">
              {drill?.title || "Detail Data Pendukung"}
            </strong>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              {loading ? "Memuat data…" : err ? <span style={{color:"var(--danger)"}}>{err}</span> :
                summary && (type === "leads"
                  ? `${fmtN(summary.count)} leads · ${fmtN(summary.students)} siswa · ${fmtIDR(summary.value)} estimasi nilai`
                  : `${fmtN(summary.count)} aktivitas ditampilkan${summary.total ? ` · ${fmtN(summary.total)} total` : ""}`
                )
              }
            </div>
          </div>
          {rows.length > 0 && (
            <button className="btn btn-ghost" onClick={onDownloadCsv} data-testid="drill-export-csv"
              style={{ padding: "6px 12px", fontSize: 12 }}>
              <Download size={13} /> Export CSV
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose} data-testid="drill-close"
            style={{ padding: "6px 10px" }}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "0 18px 18px 18px" }}>
          {!loading && rows.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
                          padding: "12px 0", position: "sticky", top: 0, background: "var(--surface)", zIndex: 2 }}
                 data-testid="drill-filters">
              <input className="select-input" placeholder="Cari di hasil…" value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="drill-search"
                style={{ minWidth: 170 }} />
              {fields.map(([k, label]) => {
                const opts = optionsFor(k);
                if (opts.length === 0) return null;
                return (
                  <select key={k} className="select-input" value={subFilters[k] || ""}
                    onChange={(e) => setSubFilters({ ...subFilters, [k]: e.target.value })}
                    data-testid={`drill-filter-${k}`} style={{ minWidth: 125 }}>
                    <option value="">{label}: Semua</option>
                    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                );
              })}
              <button className="btn btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }}
                onClick={() => { setSubFilters({}); setSearch(""); }} data-testid="drill-filter-clear">
                Reset Filter
              </button>
              <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: "auto" }}>
                {fmtN(filteredRows.length)} dari {fmtN(rows.length)}
              </span>
            </div>
          )}
          {loading && <div className="empty" style={{ padding: 40 }}>Memuat data pendukung…</div>}
          {!loading && rows.length === 0 && (
            <div className="empty" style={{ padding: 40 }}>
              Belum ada data pendukung untuk metrik ini.
            </div>
          )}
          {!loading && rows.length > 0 && filteredRows.length === 0 && (
            <div className="empty" style={{ padding: 40 }} data-testid="drill-no-match">
              Tidak ada data yang cocok dengan filter. Klik "Reset Filter".
            </div>
          )}
          {!loading && filteredRows.length > 0 && type === "leads" && (
            <div style={{ overflowX: "auto", marginTop: 8 }}>
              <table className="data-table" data-testid="drill-table-leads">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>NPSN</th>
                    <th>Status</th>
                    <th>Assigned</th>
                    <th>Source</th>
                    <th>Package</th>
                    <th>Propinsi</th>
                    <th style={{ textAlign: "right" }}>Siswa</th>
                    <th style={{ textAlign: "right" }}>Est. Value</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, i) => (
                    <tr key={r.id || i} data-testid={`drill-row-${i}`}>
                      <td><strong>{r.nama_account || "—"}</strong></td>
                      <td style={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>{r.npsn || "—"}</td>
                      <td>
                        <span className="badge" style={{
                          background: (STATUS_COLORS[r.status_lead] || "#64748b") + "22",
                          color: STATUS_COLORS[r.status_lead] || "#64748b",
                          borderColor: (STATUS_COLORS[r.status_lead] || "#64748b") + "55",
                          fontWeight: 600,
                        }}>{r.status_lead || "—"}</span>
                      </td>
                      <td>@{r.assigned_to || "—"}</td>
                      <td>{r.lead_source || "—"}</td>
                      <td style={{ fontSize: 12 }}>{r.course_package || "—"}</td>
                      <td>{r.propinsi || "—"}</td>
                      <td style={{ textAlign: "right" }}>{fmtN(r.jumlah_student)}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmtIDR(r.estimated_value)}</td>
                      <td style={{ fontSize: 11, color: "var(--muted)" }}>{fmtDate(r.last_activity_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filteredRows.length > 0 && type === "activities" && (
            <div style={{ overflowX: "auto", marginTop: 8 }}>
              <table className="data-table" data-testid="drill-table-activities">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Sales</th>
                    <th>Tim</th>
                    <th>Status</th>
                    <th>NPSN</th>
                    <th>Sekolah</th>
                    <th>Tujuan</th>
                    <th>Kegiatan</th>
                    <th>Via</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, i) => (
                    <tr key={i} data-testid={`drill-row-${i}`}>
                      <td style={{ whiteSpace: "nowrap", fontSize: 11, color: "var(--muted)" }}>{fmtDate(r.tanggal_visit)}</td>
                      <td style={{ fontWeight: 600, color: "var(--navy)" }}>{r.nama_sales || "—"}</td>
                      <td>{r.tim_sales ? <span className="badge">{r.tim_sales}</span> : "—"}</td>
                      <td><span className="badge">{r.status_sekolah || "—"}</span></td>
                      <td style={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>{r.npsn || "—"}</td>
                      <td>{r.nama_sekolah || <span style={{color:"var(--muted)",fontStyle:"italic"}}>(kosong)</span>}</td>
                      <td>{r.tujuan_sales || "—"}</td>
                      <td style={{ fontSize: 12 }}>{r.kegiatan || "—"}</td>
                      <td>{r.dilakukan_via || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
